/*
 * PageWright — global image lightbox (2026-07-22). Paul: "selecting an image in search
 * results does nothing (needs to zoom or lightbox)."
 *
 * Adapted from the never-merged feat/pw-lightbox seed (commit 8e0fc49, 2026-07-18) — same
 * capture-phase click-delegation design (intercepts the click before any bubble-phase
 * listener, e.g. the "from {book} →" link's navigation or select_item analytics, can
 * fire), same presentation-only approach (shows the SAME preview-resolution image already
 * on the page, scaled up — never fetches a higher-resolution asset; full-res files are a
 * purchased-only deliverable).
 *
 * Scope expanded 2026-07-22 to cover real collage-page artwork grids: PageWright image
 * search, the PDP "See all pages" grid, and My Library's owned-book page-preview grid.
 * Deliberately excludes My Library's grid-view book-cover thumbnails (a browsing surface,
 * not artwork) AND every generic product-card grid (.product-grid__item — search results,
 * collections) — 2026-07-26 correction: that selector was hijacking clicks meant to
 * navigate to the product page (capture-phase preventDefault/stopPropagation fired before
 * the underlying <a href> could), caught via a live repro on staging before this ever
 * shipped to production. Product search results must always navigate to the PDP; only the
 * dedicated artwork-preview surfaces below get the lightbox.
 *
 * Mounted once, site-wide, from layout/theme.liquid via snippets/pw-lightbox.liquid —
 * matches how pw-brand / pw-search-collection-analytics are already rendered there.
 */
(function () {
  'use strict';

  var SELECTOR = [
    '.pw-image-search__card img',
    '.pw-book-pages__tile img',
    '.pw-library__preview-tile img',
  ].join(', ');

  // PageWright's production backend — same site-wide-asset convention as
  // pw-search-collection-analytics.js / pw-favorites.js (no per-instance dataset backend
  // URL exists for a script loaded once from the header).
  var BACKEND_URL = (window.PW_BACKEND_URL || '')  // set once by layout/theme.liquid from the
  // per-theme `backend_url` setting. Blank DISABLES backend calls rather than falling back
  // to production — a staging theme silently writing to prod data is the bug this replaces.

  var overlay = null;
  var overlayImg = null;
  var lastFocused = null;

  function buildOverlay() {
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'pw-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image preview');
    overlay.hidden = true;

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pw-lightbox__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', close);

    overlayImg = document.createElement('img');
    overlayImg.className = 'pw-lightbox__img';
    overlayImg.alt = '';

    overlay.appendChild(closeBtn);
    overlay.appendChild(overlayImg);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    document.body.appendChild(overlay);
    return overlay;
  }

  function open(img) {
    buildOverlay();
    overlayImg.src = img.currentSrc || img.src;
    overlayImg.alt = img.alt || '';
    overlay.hidden = false;
    document.documentElement.classList.add('pw-lightbox-open');
    lastFocused = document.activeElement;
    overlay.querySelector('.pw-lightbox__close').focus();
    document.addEventListener('keydown', onKeydown);
    postAnalyticsEvent(BACKEND_URL, { event: 'lightbox_open', src: overlayImg.src });
  }

  function close() {
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    overlayImg.src = '';
    document.documentElement.classList.remove('pw-lightbox-open');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') close();
  }

  // --- analytics (dev-rules §13) — same beacon pipeline pw-favorites.js / pw-image-search.js already use ---

  var PW_SESSION_KEY = 'pw_session_id';

  function pwSessionId() {
    try {
      var id = sessionStorage.getItem(PW_SESSION_KEY);
      if (!id) {
        id = 'pw_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(PW_SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      return undefined;
    }
  }

  function pwGaClientId() {
    var m = /(?:^|;\s*)_ga=GA1\.\d\.(\d+\.\d+)/.exec(document.cookie || '');
    return m ? m[1] : undefined;
  }

  function postAnalyticsEvent(backendUrl, payload) {
    if (!backendUrl) return;
    try {
      var body = JSON.stringify(
        Object.assign({ eventVersion: 1, sessionId: pwSessionId(), clientId: pwGaClientId() }, payload),
      );
      var url = backendUrl + '/api/collect';
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics beacon must never break the lightbox */
    }
  }

  // capture phase — deliberate: fires before sibling bubble-phase listeners on the same card
  document.addEventListener('click', function (e) {
    var img = e.target.closest ? e.target.closest(SELECTOR) : null;
    if (!img) return;
    e.preventDefault();
    e.stopPropagation();
    open(img);
  }, true);
})();
